// Browse coffees. One row per CoffeeProfile; click through to CoffeeDetail.
//
// The filter bar is deliberately client-side — the operator's coffee list is
// small (dozens, not thousands) and the REST `list_coffee_profiles` endpoint
// has no filter params. Normalizing inputs to lowercase means "natural"
// spells "Natural" and "ethiopia" matches "Ethiopia".
//
// Consistency column: for each profile we fire
// `get_consistency_report(coffee_id)` in parallel after the list loads. The
// grade pill populates in-place as each report returns. Small N (dozens)
// makes parallel fan-out cheap; we cap it at a sane concurrency anyway so
// an imported-but-never-seen 200-coffee library doesn't hammer the backend.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../useApi";
import type { CoffeeProfile, ConsistencyReport } from "../types";
import { ErrorBox } from "../components/ErrorBox";

interface Filters {
  q: string;
  origin: string;
  process: string;
  tag: string;
  grade: string;
}

const EMPTY_FILTERS: Filters = {
  q: "",
  origin: "",
  process: "",
  tag: "",
  grade: "",
};

const GRADE_ORDER = [
  "excellent",
  "good",
  "variable",
  "inconsistent",
  "insufficient_data",
];

type GradeCell =
  | { status: "loading" }
  | { status: "ready"; report: ConsistencyReport | null }
  | { status: "error" };

export function CoffeeList() {
  const api = useApi();
  const [profiles, setProfiles] = useState<CoffeeProfile[]>([]);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<unknown>(null);
  // Keyed by coffee_id → loading | ready | error.
  const [grades, setGrades] = useState<Record<string, GradeCell>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .listCoffeeProfiles()
      .then((r) => {
        if (cancelled) return;
        const profs = r.profiles ?? [];
        setProfiles(profs);
        // Seed the grade map as "loading" before firing — means the column
        // renders a skeleton immediately rather than going empty → filled.
        const seed: Record<string, GradeCell> = {};
        for (const p of profs) seed[p.coffee_id] = { status: "loading" };
        setGrades(seed);
        void fetchGradesWithConcurrency(api, profs, 6, (coffeeId, cell) => {
          if (cancelled) return;
          setGrades((prev) => ({ ...prev, [coffeeId]: cell }));
        });
      })
      .catch((e) => {
        if (!cancelled) setErr(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Build filter dropdown options from the data itself so we never show an
  // origin/process the operator doesn't actually have on file.
  const { origins, processes, tags } = useMemo(
    () => buildFacets(profiles),
    [profiles],
  );

  const filtered = useMemo(
    () => applyFilters(profiles, filters, grades),
    [profiles, filters, grades],
  );

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Coffees</h1>

      <div className="filter-bar">
        <label>
          Search
          <input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="name, farm, region…"
          />
        </label>
        <label>
          Origin
          <select
            value={filters.origin}
            onChange={(e) => setFilters({ ...filters, origin: e.target.value })}
          >
            <option value="">any</option>
            {origins.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <label>
          Process
          <select
            value={filters.process}
            onChange={(e) => setFilters({ ...filters, process: e.target.value })}
          >
            <option value="">any</option>
            {processes.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label>
          Tag
          <select
            value={filters.tag}
            onChange={(e) => setFilters({ ...filters, tag: e.target.value })}
          >
            <option value="">any</option>
            {tags.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label>
          Grade
          <select
            value={filters.grade}
            onChange={(e) => setFilters({ ...filters, grade: e.target.value })}
          >
            <option value="">any</option>
            {GRADE_ORDER.map((g) => (
              <option key={g} value={g}>
                {g.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </label>
        {filtersActive(filters) && (
          <div style={{ alignSelf: "end" }}>
            <button
              className="secondary"
              onClick={() => setFilters(EMPTY_FILTERS)}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      <ErrorBox error={err} />

      <p className="muted small">
        {loading
          ? "Loading…"
          : filtered.length === profiles.length
            ? `${profiles.length} coffee${profiles.length === 1 ? "" : "s"}.`
            : `${filtered.length} of ${profiles.length} coffee${profiles.length === 1 ? "" : "s"}.`}
      </p>

      <table>
        <thead>
          <tr>
            <th>Coffee</th>
            <th>Origin</th>
            <th>Process</th>
            <th>Variety</th>
            <th>Density</th>
            <th>Moisture</th>
            <th>Consistency</th>
            <th>Tags</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((c) => (
            <tr key={c.coffee_id}>
              <td>
                <Link to={`/coffees/${encodeURIComponent(c.coffee_id)}`}>
                  {c.name}
                </Link>
                <div className="small muted mono">{c.coffee_id}</div>
              </td>
              <td>{formatOriginCell(c)}</td>
              <td>{c.process ?? "–"}</td>
              <td>{c.variety ?? "–"}</td>
              <td>{c.density_g_l != null ? `${c.density_g_l} g/L` : "–"}</td>
              <td>
                {c.moisture_pct != null ? `${c.moisture_pct}%` : "–"}
              </td>
              <td>
                <GradeCellView cell={grades[c.coffee_id]} />
              </td>
              <td>
                {(c.tags ?? []).map((t) => (
                  <span className="badge" key={t}>
                    {t}
                  </span>
                ))}
              </td>
            </tr>
          ))}
          {!loading && filtered.length === 0 && (
            <tr>
              <td colSpan={8} className="muted" style={{ textAlign: "center" }}>
                {profiles.length === 0
                  ? "No coffee profiles yet. Add one via the rate_roast or add_coffee_profile API."
                  : "No coffees match those filters."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// --- Grade cell ---------------------------------------------------------

function GradeCellView({ cell }: { cell: GradeCell | undefined }) {
  if (!cell || cell.status === "loading") {
    return (
      <span
        className="skeleton skeleton-line"
        style={{ display: "inline-block", width: 60, height: 14 }}
        aria-hidden="true"
      />
    );
  }
  if (cell.status === "error") {
    return <span className="muted small">–</span>;
  }
  if (cell.report == null) {
    return <span className="muted small">n/a</span>;
  }
  const g = cell.report.grade;
  return (
    <span className={`badge ${gradeClass(g)}`} title={`Score ${Math.round(cell.report.score)}`}>
      {g.replace(/_/g, " ")}
    </span>
  );
}

function gradeClass(grade: string): "ok" | "warn" | "err" | "" {
  switch (grade) {
    case "excellent":
    case "good":
      return "ok";
    case "variable":
      return "warn";
    case "inconsistent":
      return "err";
    default:
      return "";
  }
}

// --- Concurrent fan-out helper -----------------------------------------

// Simple concurrency limiter so a large library doesn't flood the backend.
// Resolves each request's result via `onResult` as it arrives (streaming
// into state) rather than returning the whole batch at once.
async function fetchGradesWithConcurrency(
  api: ReturnType<typeof useApi>,
  profiles: CoffeeProfile[],
  concurrency: number,
  onResult: (coffeeId: string, cell: GradeCell) => void,
): Promise<void> {
  const queue = [...profiles];
  const workers: Promise<void>[] = [];
  for (let i = 0; i < concurrency; i++) {
    workers.push(
      (async () => {
        while (queue.length) {
          const p = queue.shift();
          if (!p) break;
          try {
            const env = await api.getConsistencyReport(p.coffee_id);
            onResult(p.coffee_id, {
              status: "ready",
              report: env.data.report,
            });
          } catch {
            onResult(p.coffee_id, { status: "error" });
          }
        }
      })(),
    );
  }
  await Promise.all(workers);
}

// --- Helpers ------------------------------------------------------------

// Origin + region in one cell, comma-separated. Works for the common cases
// (origin only, origin + region, neither).
function formatOriginCell(c: CoffeeProfile): string {
  const parts = [c.origin_country, c.region].filter((x): x is string => !!x);
  return parts.length ? parts.join(", ") : "–";
}

// Distinct origin/process/tag values sorted alphabetically. Case-sensitive
// on purpose — the controlled values from the backend are already
// canonicalized.
function buildFacets(profiles: CoffeeProfile[]): {
  origins: string[];
  processes: string[];
  tags: string[];
} {
  const origins = new Set<string>();
  const processes = new Set<string>();
  const tags = new Set<string>();
  for (const c of profiles) {
    if (c.origin_country) origins.add(c.origin_country);
    if (c.process) processes.add(c.process);
    for (const t of c.tags ?? []) tags.add(t);
  }
  return {
    origins: [...origins].sort(),
    processes: [...processes].sort(),
    tags: [...tags].sort(),
  };
}

function filtersActive(f: Filters): boolean {
  return !!(f.q || f.origin || f.process || f.tag || f.grade);
}

function applyFilters(
  profiles: CoffeeProfile[],
  f: Filters,
  grades: Record<string, GradeCell>,
): CoffeeProfile[] {
  const q = f.q.trim().toLowerCase();
  return profiles.filter((c) => {
    if (f.origin && c.origin_country !== f.origin) return false;
    if (f.process && c.process !== f.process) return false;
    if (f.tag && !(c.tags ?? []).includes(f.tag)) return false;
    if (f.grade) {
      const cell = grades[c.coffee_id];
      // Filter doesn't hide loading rows — the operator probably wants to
      // see the whole list while grades stream in. Once a cell resolves
      // we hold it to the filter.
      if (cell && cell.status === "ready") {
        const g = cell.report?.grade ?? "insufficient_data";
        if (g !== f.grade) return false;
      } else if (cell && cell.status === "error") {
        return false;
      }
    }
    if (q) {
      const hay = [
        c.name,
        c.coffee_id,
        c.origin_country,
        c.region,
        c.farm,
        c.variety,
      ]
        .filter((x): x is string => !!x)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}
